import { Dialog, DialogClose, DialogTitle, useDialogClose } from "@suse-o11y/dialog";
import { IconClose } from "@suse-o11y/icons";
import { usePermanent } from "@suse-o11y/use-permanent";
import React from "react";

import { Form } from "~/components/form";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { DashboardVariable } from "../../common/dashboard-spec";
import { DashboardTemplateVariablesBoundary } from "../../routes/dashboards/dashboard/template-variables-boundary";
import { notification } from "../react/notification/Notification";

import { DrawerContent } from "./_drawer-content";
import { FormContextProvider, type FormContextValue } from "./_form-context";
import { Processor } from "./_form-state";

export interface SetupDashboardVariableDrawerProps {
	timeRange: TimeRange;
	initial?: DashboardVariable;
	// keep the draft of all variables for cases when a variable references another variable
	variables: ReadonlyArray<DashboardVariable>;
	onSave: (variable: DashboardVariable) => void;
}

export const SetupDashboardVariableDrawer: React.FC<SetupDashboardVariableDrawerProps> = (props) => (
	<Dialog className="drawer/dialog drawer-wide" data-beest-testid="setup-dashboard-variable-drawer">
		<SetupDashboardVariableContent {...props} />
	</Dialog>
);

const SetupDashboardVariableContent: React.FC<SetupDashboardVariableDrawerProps> = ({
	timeRange,
	initial,
	variables,
	onSave,
}) => {
	const isEditing = initial != null;
	const close = useDialogClose();
	const state = usePermanent(() => Processor.fromSpec(initial));
	const context = React.useMemo<FormContextValue>(() => ({ timeRange }), [timeRange]);

	return (
		<DashboardTemplateVariablesBoundary timeRange={timeRange} variables={variables}>
			<Form
				className="contents"
				onSubmit={(output) => {
					const variable = DashboardVariable.of(Processor.toSpec(output));

					if (variable == null) {
						// should never happen but it is better to gracefully handle it
						notification.error({
							message: "Cannot save variable: invalid configuration",
						});
					} else {
						onSave(variable);
						close();
					}
				}}
				state={state}
			>
				<FormContextProvider value={context}>
					<header className="drawer-header">
						<DialogTitle className="drawer-title">
							{isEditing ? "Edit Dashboard Variable" : "Add Dashboard Variable"}
						</DialogTitle>

						<DialogClose aria-label="Close" className="button button-square ml-auto">
							<IconClose className="size-3" />
						</DialogClose>
					</header>

					<DrawerContent className="drawer-body" isEditing={isEditing} state={state} />

					<footer className="drawer-footer">
						<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

						<button className="button button-primary uppercase" type="submit">
							{isEditing ? "Update variable" : "Create variable"}
						</button>
					</footer>
				</FormContextProvider>
			</Form>
		</DashboardTemplateVariablesBoundary>
	);
};
